'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { Product, formatPrice, getVariantPrice } from '@/lib/products';
import { useTranslation } from '@/context/LanguageContext';
import { trackProductCardClicked } from '@/lib/analytics';
import styles from './ProductCard.module.scss';

export const PRODUCT_CARD_CTA_FLAG = 'product-card-cta';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { t, currency } = useTranslation();
  const ctaVariant = useFeatureFlagVariantKey(PRODUCT_CARD_CTA_FLAG);
  const showCta = ctaVariant === 'test';
  const lowestPrice = Math.min(...product.variants.map((v) => getVariantPrice(v, currency)));
  const isComingSoon = !Number.isFinite(lowestPrice) || lowestPrice <= 0;
  const cardLabel = product.cardLabel?.trim();
  const isDarkRoast = /m\u00f8rk|dark|dunkel|dunkle/i.test(cardLabel ?? '');
  const baseImageSrc = product.image?.trim() || '';
  const cardImageSrc = product.cardImage?.trim() || '';
  const mainImageSrc = cardImageSrc || baseImageSrc;
  const hoverImageSrc = cardImageSrc && baseImageSrc ? baseImageSrc : '';

  const handleClick = () => {
    trackProductCardClicked(
      { id: product.id, name: product.name, price: lowestPrice },
      currency,
      ctaVariant
    );
  };

  return (
    <Link href={`/shop/${product.slug}`} className={styles.card} onClick={handleClick}>
      <div className={styles.imageWrap}>
        {mainImageSrc && (
          <Image
            src={mainImageSrc}
            alt={product.name}
            width={400}
            height={400}
            className={styles.mainImage}
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
        )}
        {hoverImageSrc && (
          <Image
            src={hoverImageSrc}
            alt={product.name}
            width={400}
            height={400}
            className={styles.hoverImage}
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
        )}
      </div>
      <div className={styles.info}>
        <h3 className={styles.name}>{product.name}</h3>
        <p className={styles.flavor}>{product.flavor}</p>
        <div className={styles.meta}>
          {cardLabel && (
            <span className={`${styles.roast} ${isDarkRoast ? styles.roastDark : ''}`}>
              {cardLabel}
            </span>
          )}
          {isComingSoon ? (
            <span className={styles.price}>{t.productCard.comingSoon}</span>
          ) : (
            <span className={styles.price}>{t.productCard.pricePrefix} {formatPrice(lowestPrice, t)}</span>
          )}
        </div>
        {showCta && (
          <span className={styles.cta}>
            {t.productCard.cta}
            <svg
              className={styles.ctaArrow}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        )}
      </div>
    </Link>
  );
}
